<template>
  <v-container fluid>
    <v-row v-if="isLoading">
      <v-col>
        <loading-spinner></loading-spinner>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" md="6">
        <paginated-table
          :data="galleries"
          :columns="tableColumns"
          :title="$t('title.conference_gallery_management')"
          @updatePagination="updatePagination"
        ></paginated-table>
      </v-col>

      <v-col cols="12" md="6">
        <v-card>
          <v-form ref="form" v-model="valid">
            <v-card-text>
              <v-row>
                <v-col>
                  <v-text-field
                    v-model="gallery.title"
                    :label="$t('label.title')"
                    type="text"
                    :rules="[rules.required]"
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <input type="file" @change="onFileChange" accept="image/*" />

                  <!-- 업로드된 이미지 미리보기 -->
                  <div v-if="gallery.image">
                    <img
                      :src="gallery.image"
                      alt="Uploaded Image"
                      style="max-width: 100%; height: auto"
                    />
                  </div>
                  <!-- 오류 메시지 표시 -->
                  <div v-if="errorMessage" style="color: red; margin-top: 10px">
                    {{ errorMessage }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-form>

          <v-card-actions class="mt-3">
            <v-spacer></v-spacer>
            <custom-button
              @click="handleSubmit"
              :label="$t('button.save')"
              :disabled="!valid"
              color="primary"
              size="lg"
              gradient
              rounded
            ></custom-button>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PaginatedTable from "@/components/Tables/PaginatedTable.vue";
import CustomButton from "@/components/common/buttons/CustomButton.vue";
import LoadingSpinner from "@/components/common/Spinners/LoadingSpinner.vue";

import { getConferenceGalleryColumns } from "@/views/AdminView/columns";

import { galleryApi } from "@/api/api";
import { deletePopup, success, savePopup } from "@/views/AdminView/popups";

export default {
  components: {
    PaginatedTable,
    CustomButton,
    LoadingSpinner,
  },
  data() {
    return {
      // labels
      titles: {
        addCardTitle: "제목",
        tableTitle: "갤러리 관리",
      },

      buttons: {
        submit: "저장",
      },

      // boolean
      responsive: false,
      isLoading: false,

      // fields & columns
      tableColumns: getConferenceGalleryColumns({
        edit: this.handleEdit,
        delete: this.handleDelete,
      }),

      // init data
      errorMessage: null, // 오류 메시지

      galleries: {
        page_info: {
          page: 1,
          size: 5,
          total_elements: 5,
          total_pages: 1,
        },
        elements: [],
      },
      gallery: {
        idx: null,
        title: null,
        image: null,
        created_at: null,
      },
      rules: {
        required: (v) => !!v || "값을 입력하세요.",
      },
      valid: false,
    };
  },
  methods: {
    onFileChange(event) {
      const file = event.target.files[0];

      // 초기화
      this.errorMessage = null;
      this.gallery.image = null;

      if (!file) return;

      // 1. 파일 크기 제한
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        this.errorMessage = "파일 크기는 5MB를 초과할 수 없습니다.";
        return;
      }

      // 2. 이미지 파일 형식 확인
      if (!file.type.startsWith("image/")) {
        this.errorMessage = "이미지 파일만 업로드 가능합니다.";
        return;
      }

      // 3. Base64 변환
      const reader = new FileReader();
      reader.onload = (e) => {
        this.gallery.image = e.target.result; // Base64 데이터 저장
      };
      reader.readAsDataURL(file); // 파일을 Base64로 읽기
    },

    /**
     * *******************************************************************************************
     *  버튼 액션
     * *******************************************************************************************
     */
    // 수정 버튼 액션
    handleEdit(gallery) {
      this.fetchGallery(gallery);
    },

    async handleDelete(gallery) {
      const isConfirmed = await deletePopup(gallery);

      if (!isConfirmed) {
        return;
      }

      const isSuccess = await this.deleteGallery(gallery);

      if (isSuccess) {
        await this.fetchGalleries();
        await success();
      }
    },

    // 완료 버튼액션
    async handleSubmit() {
      if (this.gallery.image === null) {
        alert("이미지를 추가하세요.");
        return;
      }

      const isConfirmed = await savePopup();

      if (!isConfirmed) {
        return;
      }

      if (this.gallery.idx === null) {
        const isSuccess = await this.createGallery(this.gallery);

        if (isSuccess) {
          this.initData();
          await this.fetchGalleries();
          await success();
        }
        return;
      }

      const isSuccess = await this.updateGallery(this.gallery);

      if (isSuccess) {
        await this.fetchGallery(this.gallery);
        await success();
      }
    },

    /**
     * *******************************************************************************************
     *  API 요청
     * *******************************************************************************************
     */

    // 목록 조회
    async fetchGalleries() {
      this.isLoading = true;
      const params = {
        size: this.galleries.page_info.size,
        page: this.galleries.page_info.page,
      };

      try {
        const response = await galleryApi.showAll(params);
        if (response && response.status === 200) {
          this.galleries = response.data.response;
          this.isLoading = false;
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    fetchGallery(gallery) {
      this.isLoading = true;
      try {
        galleryApi.showGallery(gallery).then((response) => {
          if (response && response.status === 200) {
            this.gallery = response.data.response;
            return true;
          }
          return false;
        });
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    async createGallery(gallery) {
      try {
        this.isLoading = true;
        const response = await galleryApi.createGallery(gallery);
        if (response && response.status === 201) {
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    async updateGallery(gallery) {
      try {
        this.isLoading = true;
        const response = await galleryApi.updateGallery(gallery);
        if (response && response.status === 200) {
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    async deleteGallery(gallery) {
      try {
        this.isLoading = true;
        const response = await galleryApi.deleteGallery(gallery);
        if (response && response.status === 204) {
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * *******************************************************************************************
     *  기타 메서드
     * *******************************************************************************************
     */

    /**
     * Pagination 타입 명세
     * @typedef {Object} Conditions
     * @property {string} page - 페이지
     * @property {string} size - 사이즈
     * @property {string} total_elements - 전체 데이터 수
     * @property {string} total_pages - 전체 페이지 수
     */

    /**
     * 페이지네이션 업데이트 메서드
     *
     * @description: 페이징 정보가 변경되면 하위 컴포넌트에서 정보를 받아 업데이트 및 API 요청
     * @params {Pagination} pagination
     */
    updatePagination(pagination) {
      this.galleries.page_info.size = pagination.size;
      this.galleries.page_info.page = pagination.page;

      this.fetchGalleries();
    },
    initData() {
      this.gallery = {
        idx: null,
        title: null,
        image: null,
        created_at: null,
      };
    },
  },
  mounted() {
    this.fetchGalleries();
  },
};
</script>

<style lang="css" scoped>
.form-control-static {
  margin-top: 6px;
}

.static-label {
  text-align: left;
  align-self: left;
}

.layout-center {
  display: flex;
  justify-content: center;
  height: auto;
}

.button-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

img {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 5px;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
